import React, { useContext, useState, useEffect } from "react";
import { More } from "../../assets/svgs/icons";
import { Round as BadgeRound } from "../badge";
import { formatDateStringShort } from "../../utils/commonFunctions";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../../context/MyContext";
import { SubscriptionApi } from "../../views/subscription/utils";
import type { UserSubscriptionStatus } from "../../views/subscription/utils";
import WebUser from "./WebUser";

export interface UserProps {
  state?:
    | "inactive"
    | "active"
    | "expired"
    | "new"
    | "on_hold"
    | "canceled"
    | "trial_active"
    | "trial_cancelled";
  email?: string;
  avatarUrl?: string;
  endDate?: string; // For active state - format: "YYYY-MM-DD"
  startDate?: string; // Optional: For future use
  showButton?: boolean;
  showDetail?: boolean;
  onMoreClick?: () => void;
  onPurchaseClick?: () => void;
  onStateChange?: (newState: string) => void;
}

const User: React.FC<UserProps> = ({
  state: propState = "inactive",
  email = "donga@donga.com",
  avatarUrl,
  endDate: propEndDate = "",
  startDate: propStartDate = "",
  showButton = true,
  showDetail = false,
  onMoreClick,
  onStateChange,
}) => {
  const { isAppMode, user, isUnderVersion } = useContext(MyContext);
  const navigate = useNavigate();

  const [state, setState] = useState<string>(propState);
  const [startDate, setStartDate] = useState<string>(propStartDate);
  const [endDate, setEndDate] = useState<string>(propEndDate);
  const [showWebUser, setShowWebUser] = useState(false);
  const isNew = ["new", "inactive", "expired"].includes(state);

  useEffect(() => {
    if (isAppMode && !isUnderVersion) return;

    setShowWebUser(false);
    setState(propState);
    setStartDate(propStartDate);
    setEndDate(propEndDate);
  }, [isAppMode, isUnderVersion, propState, propStartDate, propEndDate]);

  // 유저 상태 조회 API 호출
  useEffect(() => {
    const fetchUserSubscriptionStatus = async () => {
      if (!isAppMode || isUnderVersion) return;
      if (!user?.id) {
        setState("inactive");
        return;
      }

      try {
        const response = await SubscriptionApi.get(
          `/billing/user-status?userId=${encodeURIComponent(user.id)}`,
        );

        if (!response.ok) {
          console.error("유저 상태 조회 실패:", response.status);
          return;
        }

        const data = (await response.json()) as UserSubscriptionStatus;

        // subscriptionStatus를 컴포넌트 상태로 매핑
        let mappedState: string;
        switch (data.subscriptionStatus) {
          case "active":
            mappedState = "active";
            break;
          case "canceled":
            mappedState = "canceled";
            break;
          case "payment_failed":
            mappedState = "on_hold";
            break;
          case "expired":
            mappedState = "expired";
            break;
          case "none":
          default:
            mappedState = "inactive";
            break;
        }

        if (
          (mappedState === "inactive" || mappedState === "expired") &&
          propState !== "inactive"
        ) {
          setShowWebUser(true);
          return;
        }

        setShowWebUser(false);
        setState(mappedState);
        onStateChange?.(mappedState);
        setStartDate(data.startDate);
        setEndDate(data.expiryDate);
      } catch (err) {
        console.error("유저 상태 조회 중 오류:", err);
        setState("inactive");
      }
    };

    fetchUserSubscriptionStatus();
  }, [isAppMode, isUnderVersion, user?.id]);

  const onPurchaseClick = () => {
    if (isAppMode && !isUnderVersion) {
      navigate("/subscription");
      return;
    }
    window.open(
      `${process.env.REACT_APP_DONGA_STORE}/subscription?anchor=2`,
      "_blank",
    );
  };

  const handleSubscriptionManagement = () => {
    if (!isAppMode || isUnderVersion) {
      alert("이 기능은 앱에서만 사용 가능합니다.");
      return;
    }
    // (window as any)?.Android?.openPlayStorePaymentHistory();
    (window as any)?.Android?.openSubscriptionManagement();
  };

  const getNextDate = (dateString: string) => {
    const date = new Date(dateString);
    date.setDate(date.getDate());
    return formatDateStringShort(date.toISOString().split("T")[0]);
  };

  // 웹 모드일 경우 WebUser 렌더링
  if (!isAppMode || isUnderVersion || showWebUser) {
    return (
      <WebUser
        state={state as "inactive" | "active" | "expired"}
        email={email}
        avatarUrl={avatarUrl}
        endDate={endDate}
        showButton={showButton}
        onMoreClick={onMoreClick}
      />
    );
  }

  // 앱 모드일 경우 App User 렌더링
  return (
    <>
      <div className="bg-white box-border flex gap-4 items-center justify-center pl-4 pr-0 py-4 w-full">
        {/* Container */}
        <div className="flex-1 bg-white flex gap-[10px] h-12 items-center rounded-[24px]">
          {/* Avatar */}
          <div className="border border-[#e2d5fc] rounded-[24px] shrink-0 w-12 h-12 overflow-hidden">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="User avatar"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#cd3ec7] via-[#5328df] to-[#2d8efa] flex items-center justify-center text-white font-bold text-lg">
                U
              </div>
            )}
          </div>

          {/* User Info */}
          <div className="flex-1 flex flex-col gap-1 items-start justify-center">
            {/* Validity Section */}
            <div className="flex gap-1 items-center">
              {/* 신규 inactive 상태, 만료 expired 상태 */}
              {isNew && (
                <button onClick={onPurchaseClick} className="cursor-pointer">
                  <p
                    className="bg-clip-text bg-gradient-to-r font-bold from-[#cd3ec7] leading-5 text-[14px] text-center to-[#2d8efa] tracking-[-0.28px] via-50% via-[#5328df] whitespace-nowrap underline"
                    style={{
                      WebkitTextFillColor: "transparent",
                      textDecorationColor: "#5328df",
                    }}
                  >
                    구독하고 바로 시작해보세요!
                  </p>
                </button>
              )}

              {/* 이용 중 active 상태 */}
              {state === "active" && (
                <div className="flex flex-col gap-1 w-full">
                  {/* First line: Badge + Date Range */}
                  <div className="flex gap-1 items-center">
                    <BadgeRound size="sm" style="line" color="main">
                      이용중
                    </BadgeRound>
                    <span className="text-[#212121] text-[14px]">{`${formatDateStringShort(
                      startDate,
                    )} ~ ${formatDateStringShort(endDate)}`}</span>
                  </div>
                </div>
              )}
              {/* 결제 실패 on_hold 상태 */}
              {state === "on_hold" && (
                <div className="flex flex-col gap-1 w-full">
                  {/* First line: Warning message */}
                  <div className="flex gap-1 items-center">
                    <BadgeRound size="sm" style="line" color="gray">
                      결제실패
                    </BadgeRound>
                    <span className="text-[#212121] text-[14px]">{`${formatDateStringShort(
                      startDate,
                    )} ~ ${formatDateStringShort(endDate)}`}</span>
                  </div>
                </div>
              )}
              {/* 해지 예약 canceled 상태 */}
              {state === "canceled" && (
                <div className="flex flex-col gap-1 w-full">
                  <div className="flex gap-1 items-center">
                    <BadgeRound size="sm" style="line" color="main">
                      이용중
                    </BadgeRound>
                    <span className="text-[#212121] text-[14px]">{`${formatDateStringShort(
                      startDate,
                    )} ~ ${formatDateStringShort(endDate)}`}</span>
                  </div>
                </div>
              )}
              {/* 무료체험유지 trial_active 상태 */}
              {state === "trial_active" && (
                <div className="flex flex-col gap-1 w-full">
                  {/* First line: Badge + "무료체험 중" text + Date Range */}
                  <div className="flex gap-1 items-center">
                    <BadgeRound size="sm" style="line" color="main">
                      이용중
                    </BadgeRound>
                    <span className="text-[#212121] text-[14px]">
                      무료체험 중
                    </span>
                  </div>
                  <div className="flex gap-1 items-center">
                    <span className="text-[#888888] text-[12px]">
                      {`${formatDateStringShort(
                        startDate,
                      )} ~ ${formatDateStringShort(endDate)}`}
                    </span>
                  </div>
                </div>
              )}
              {/* 무료체험해지 trial_cancelled 상태 */}
              {state === "trial_cancelled" && (
                <div className="flex flex-col gap-1 w-full">
                  {/* First line: Badge + "무료체험 중" text + Date Range */}
                  <div className="flex gap-1 items-center">
                    <BadgeRound size="sm" style="line" color="main">
                      이용중
                    </BadgeRound>
                    <span className="text-[#212121] text-[14px]">
                      무료체험 중
                    </span>
                  </div>
                  <div className="flex gap-1 items-center">
                    <span className="text-[#888888] text-[12px]">
                      {`${formatDateStringShort(
                        startDate,
                      )} ~ ${formatDateStringShort(endDate)}`}
                    </span>
                    <button
                      onClick={handleSubscriptionManagement}
                      className="text-[#212121] text-[14px] underline transition-colors cursor-pointer"
                    >
                      구독관리
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Email */}
            <p className="font-regular text-[#888888] text-[12px] text-center tracking-[-0.24px] leading-4 whitespace-nowrap">
              {email}
            </p>
          </div>
        </div>

        {/* More Button */}
        {showButton && (
          <button
            onClick={onMoreClick}
            className="box-border flex gap-[10px] items-center justify-center p-3 shrink-0 size-12 hover:bg-[#f7f7f7] rounded-full transition-colors"
            aria-label="More options"
          >
            <More width={24} height={24} className="text-[#212121] rotate-90" />
          </button>
        )}
      </div>
      {/* detail 정보 */}
      {showDetail && isAppMode && !isNew && (
        <div className={"side-detail-area"}>
          {["active", "trial_active"].includes(state) && (
            <div className="flex gap-1 items-center">
              <span className="purple-text">
                {endDate ? `${getNextDate(endDate)}` : ""} 결제예정
              </span>
              <button
                onClick={handleSubscriptionManagement}
                className="manage-text"
              >
                구독관리
              </button>
            </div>
          )}
          {/* 결제 실패 */}
          {state === "on_hold" && (
            <div className="flex gap-1 items-center">
              <span className="flex items-center gap-1">
                <img
                  alt="warning"
                  src="https://img.dongascience.com/air/purple_warning.svg"
                />
                <span className="light-gray-text">
                  결제 수단을 확인해주세요.
                </span>
              </span>
              <button
                onClick={handleSubscriptionManagement}
                className="manage-text"
              >
                구독관리
              </button>
            </div>
          )}
          {/* 해지 예약 */}
          {state === "canceled" && (
            <div className="flex gap-1 items-center">
              <button
                onClick={handleSubscriptionManagement}
                className="manage-text"
              >
                구독관리
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default User;
